import React, { useEffect, useRef, useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { WS_URL, resolveUrl } from '../utils/api';
import CallOverlay from '../components/CallOverlay';
import { useWebRTC } from '../hooks/useWebRTC';
import { NotificationContext, ConfirmContext } from '../components/Layout';
import { getTheme } from '../utils/theme';

import UserProfileModal from '../components/UserProfileModal';
import VideoTrimmer from '../components/VideoTrimmer';

const theme = getTheme();

// ─── WS singleton: persists across navigation ─────────────────────────────
// This prevents the chat from disconnecting when user navigates to other pages.
let _globalWs       = null;
let _globalRoomId   = null;
let _globalStatus   = 'idle';
let _globalPartner  = null;
let _globalMessages = [];
const _listeners    = new Set();

function notifyListeners() {
  _listeners.forEach(fn => fn());
}

function getGlobalState() {
  return { ws: _globalWs, roomId: _globalRoomId, status: _globalStatus, partner: _globalPartner, messages: _globalMessages };
}
// ─────────────────────────────────────────────────────────────────────────────



const myId   = () => parseInt(localStorage.getItem('userId'));
const myName = () => localStorage.getItem('username');
const STATUS = { IDLE: 'idle', SEARCHING: 'searching', CHATTING: 'chatting' };

function RCVideoPlayer({ src, style }) {
  const [err, setErr] = React.useState(false);
  if (err) return <div style={{ fontSize: 10, color:'#64748b', padding:'6px 0' }}>⚠️ Video unavailable</div>;
  return <video src={src} controls playsInline style={style} onError={() => setErr(true)} />;
}

function Avatar({ url, name, size = 40, onClick }) {
  const src = resolveUrl(url);
  if (src) return <img src={src} alt={name} onClick={onClick} style={{ width:size, height:size, borderRadius:'50%', objectFit:'cover', flexShrink:0, cursor: onClick?'pointer':'inherit' }} onError={e => e.target.style.display='none'} />;
  return <div onClick={onClick} style={{ width:size, height:size, borderRadius:'50%', background:theme.colors.gradient, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:'#fff', fontSize:size*0.38, flexShrink:0, cursor: onClick?'pointer':'inherit' }}>{name?.[0]?.toUpperCase()||'?'}</div>;
}

// Lightbox
function Lightbox({ src, onClose }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.95)', zIndex:50000, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={onClose}>
      <img src={src} alt="full" style={{ maxWidth:'95vw', maxHeight:'90vh', objectFit:'contain', borderRadius:8 }} onClick={e => e.stopPropagation()} />
      <button style={{ position:'absolute', top:16, right:16, background:'rgba(255,255,255,0.15)', border:'none', borderRadius:'50%', width:36, height:36, color:'#fff', fontSize: 16, cursor:'pointer' }} onClick={onClose}>✕</button>
    </div>
  );
}

export default function RandomChat() {
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext) || {};
  const { confirm } = useContext(ConfirmContext) || {};

  // Local state synced from global singleton
  const [status, setStatus]     = useState(_globalStatus);
  const [messages, setMessages] = useState(_globalMessages);
  const [partner, setPartner]   = useState(_globalPartner);
  const [newMsg, setNewMsg]     = useState('');
  const [friendReq, setFriendReq] = useState(null);
  const [notification, setNotification] = useState('');
  const [uploading, setUploading] = useState(false);
  const [ctxMenu, setCtxMenu]   = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [partnerIsTyping, setPartnerIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [showTrimmer, setShowTrimmer] = useState(false);

  const wsRef        = useRef(_globalWs);
  const bottomRef    = useRef(null);
  const fileInputRef = useRef(null);
  const token        = localStorage.getItem('token');
  const rtc          = useWebRTC(wsRef, null);


  const showMsg = useCallback((m, dur = 4000) => {
    setNotification(m);
    if (dur) setTimeout(() => setNotification(''), dur);
  }, []);

  // Sync state from global
  const syncFromGlobal = useCallback(() => {
    setStatus(_globalStatus);
    setMessages([..._globalMessages]);
    setPartner(_globalPartner);
    wsRef.current = _globalWs;
  }, []);

  // Connect/maintain global WS
  const connectWS = useCallback(() => {
    if (_globalWs && _globalWs.readyState !== WebSocket.CLOSED) {
      wsRef.current = _globalWs;
      return;
    }
    const ws = new WebSocket(`${WS_URL}/ws/random-chat/?token=${token}`);
    _globalWs = ws;
    wsRef.current = ws;

    ws.onopen = () => {
      if (_globalRoomId) {
        ws.send(JSON.stringify({ type: 'rejoin_room', room_id: _globalRoomId }));
        _globalStatus = STATUS.CHATTING;
        notifyListeners();
      }
    };

    ws.onmessage = async (e) => {
      const data = JSON.parse(e.data);
      switch (data.type) {
        case 'searching':
          _globalStatus = STATUS.SEARCHING;
          notifyListeners();
          showMsg('Searching for a stranger...');
          break;
        case 'partner_joined':
          _globalStatus  = STATUS.CHATTING;
          _globalPartner = { username: data.partner_username, realUsername: data.real_partner_username, areFriends: data.are_friends, avatar: data.partner_avatar, id: data.partner_id };
          _globalMessages = [];
          _globalRoomId  = null;
          notifyListeners();
          setFriendReq(null);
          showMsg(`Connected with ${data.partner_username}`, 3000);
          break;
        case 'chat_ended':
          _globalStatus  = STATUS.IDLE;
          _globalPartner = null;
          _globalRoomId  = null;
          _globalMessages = [];
          notifyListeners();
          showMsg('Chat ended.');
          break;
        case 'partner_disconnected':
          _globalStatus = STATUS.SEARCHING;
          notifyListeners();
          showMsg('Stranger left. Finding someone new...');
          setTimeout(() => { if (_globalWs?.readyState === WebSocket.OPEN) _globalWs.send(JSON.stringify({ type: 'start_chat' })); }, 1000);
          break;
        case 'message':
          if (!_globalMessages.find(m => m.id === data.message_id)) {
            _globalMessages = [..._globalMessages, { id: data.message_id, sender_id: data.sender_id, content: data.message, timestamp: data.timestamp, file_url: data.file_url, file_type: data.file_type, original_filename: data.original_filename, is_read: data.is_read, is_unsent: false }];
            notifyListeners();
          }
          ws.send(JSON.stringify({ type: 'mark_message_read', message_id: data.message_id }));
          break;
        case 'message_deleted':
          _globalMessages = _globalMessages.filter(m => m.id !== data.message_id);
          notifyListeners();
          break;
        case 'message_read':
          _globalMessages = _globalMessages.map(m => m.sender_id === myId() ? { ...m, is_read: true } : m);
          notifyListeners();
          break;
        case 'friend_request':
          setFriendReq({ id: data.request_id, status: data.status });
          if (data.status === 'received') showMsg(`New friend request from ${_globalPartner?.username || 'Stranger'}!`);
          if (data.status === 'sent') showMsg('Friend request sent!');
          break;
        case 'friend_request_accepted':
          setPartner(prev => prev ? { ...prev, areFriends: true, id: data.sender_id || prev.id } : prev);
          if (_globalPartner) _globalPartner = { ..._globalPartner, areFriends: true, id: data.sender_id || _globalPartner.id };
          showMsg(`${data.sender_username} is now your friend!`);
          setFriendReq({ status: 'accepted' });
          break;
        case 'friend_request_unsent':
          setFriendReq(null);
          break;
        case 'typing_status':
          if (data.sender_id !== myId()) {
            setPartnerIsTyping(data.is_typing);
          }
          break;
        case 'call_request': case 'call_accept': case 'call_reject': case 'call_end':
        case 'webrtc_offer': case 'webrtc_answer': case 'webrtc_ice_candidate':
          rtc.handleSignal(data);
          break;
        default: break;
      }
    };

    ws.onclose = () => {
      if (_globalWs === ws) _globalWs = null;
      // Auto-reconnect if user still on page and was connected
      setTimeout(() => {
        if (localStorage.getItem('token') && _globalStatus !== STATUS.IDLE) connectWS();
      }, 3000);
    };
  }, [rtc, showMsg, token]);

  // Subscribe to global state changes
  useEffect(() => {
    const listener = () => syncFromGlobal();
    _listeners.add(listener);
    return () => _listeners.delete(listener);
  }, [syncFromGlobal]);

  // On mount: check active room, then connect
  useEffect(() => {
    api.get('/active_room/').then(res => {
      if (res.data.room_id) {
        _globalRoomId  = res.data.room_id;
        _globalStatus  = STATUS.CHATTING;
        _globalPartner = { username: res.data.partner_username, realUsername: res.data.partner_username, areFriends: res.data.are_friends, avatar: res.data.partner_avatar, id: res.data.partner_id };
        notifyListeners();
      }
      connectWS();
    }).catch(() => connectWS());
    // FIX: do NOT close WS on unmount — it must persist across navigation
    return () => {};
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const startChat = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      connectWS();
      setTimeout(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'start_chat' }));
          _globalStatus = STATUS.SEARCHING; notifyListeners();
        }
      }, 600);
      return;
    }
    wsRef.current.send(JSON.stringify({ type: 'start_chat' }));
    _globalStatus = STATUS.SEARCHING; notifyListeners();
  };

  const endChat = async (skipConfirm = false) => {
    if (!skipConfirm && _globalStatus === STATUS.CHATTING) {
      const ok = await confirm({ title: 'End Chat?', message: 'Are you sure you want to leave this conversation?', icon: '🔚', confirmText: 'End Chat' });
      if (!ok) return;
    }
    if (wsRef.current?.readyState === WebSocket.OPEN)
      wsRef.current.send(JSON.stringify({ type: 'end_chat' }));
    _globalStatus  = STATUS.IDLE;
    _globalPartner = null;
    _globalRoomId  = null;
    _globalMessages = [];
    notifyListeners();
    setFriendReq(null);
  };

  const nextChat = useCallback(() => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: 'end_chat' }));
    setTimeout(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'start_chat' }));
        _globalStatus = STATUS.SEARCHING; notifyListeners();
      }
    }, 200);
  }, []);

  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: true }));
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: false }));
        }
      }, 3000);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !wsRef.current || status !== STATUS.CHATTING) return;
    if (wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: 'message', message: newMsg.trim(), sender_username: myName() }));
    setNewMsg('');
  };

  const getLimit = (f) => {
    if (f.type.startsWith('video/')) return 5 * 1024 * 1024;
    if (f.type.startsWith('image/')) return 1 * 1024 * 1024;
    return 1 * 1024 * 1024;
  };

  const sendFile = async (file) => {
    if (!file) return;

    const limit = getLimit(file);
    if (file.size > limit) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      const limitMB = limit / (1024 * 1024);
      showMsg(`File is too large (${sizeMB}MB). Max limit for ${file.type.startsWith('video/') ? 'video' : 'this file'} is ${limitMB}MB. Please trim or compress it.`, 6000);
      setPendingFile(file);
      setShowTrimmer(true);
      return;
    }

    await doSendFile(file);
  };

  const doSendFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('chat_type', 'random');
      const res = await api.post('/upload_file/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'message', message: '', file_url: res.data.file_url, file_type: res.data.file_type, original_filename: res.data.original_filename, file_path: res.data.file_path, sender_username: myName() }));
      }
    } catch { showMsg('Upload failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleTrimmerDone = (blob, name, type) => {
    setShowTrimmer(false);
    setPendingFile(null);
    const limit = getLimit({ type });
    if (blob.size > limit) {
      showMsg(`Processed file still exceeds ${limit / (1024 * 1024)}MB. Please trim more.`, 6000);
      return;
    }
    doSendFile(new File([blob], name, { type }));
  };

  const sendFriendRequest = () => {
    if (!partner?.realUsername || partner.areFriends) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'friend_request', receiver_username: partner.realUsername }));
      setFriendReq({ status: 'sent' });
    }
  };

  const handleFriendResponse = (accept) => {
    if (wsRef.current?.readyState === WebSocket.OPEN)
      wsRef.current.send(JSON.stringify({ type: accept ? 'accept_friend_request' : 'reject_friend_request', request_id: friendReq?.id }));
    if (accept) {
      setPartner(prev => prev ? { ...prev, areFriends: true } : prev);
      if (_globalPartner) _globalPartner = { ..._globalPartner, areFriends: true };
      setFriendReq({ status: 'accepted' });
    } else setFriendReq(null);
  };

  const unsendMessage = async (msgId) => {
    const ok = await confirm({ title: 'Unsend Message?', message: 'This will remove the message for everyone.', icon: '↩️', confirmText: 'Unsend' });
    if (!ok) return;
    if (wsRef.current) wsRef.current.send(JSON.stringify({ type: 'unsend_message', message_id: msgId }));
    setCtxMenu(null);
  };

  const deleteMessage = async (msgId) => {
    const ok = await confirm({ title: 'Delete Message?', message: 'Remove this message from your chat?', icon: '🗑️', confirmText: 'Delete' });
    if (!ok) return;
    if (wsRef.current) wsRef.current.send(JSON.stringify({ type: 'delete_message', message_id: msgId }));
    setCtxMenu(null);
  };

  return (
    <div style={S.root} onClick={() => setCtxMenu(null)}>
      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
      {notification && <div style={S.notif}>{notification}</div>}

      <div style={S.header}>
        <div style={S.headerLeft}>
          {partner ? (
            <><Avatar url={null} name={partner.username} size={42} />
              <div>
                <div style={S.partnerName}>{partner.areFriends ? partner.realUsername : partner.username}</div>
                <div style={S.partnerSub}>{status === STATUS.CHATTING ? '● Connected' : '○ Disconnected'}</div>
              </div></>
          ) : <span style={S.title}>Random Chat</span>}
        </div>
        <div style={S.hActions}>
          {status === STATUS.CHATTING && partner && (<>
            {!partner.areFriends && (!friendReq || friendReq.status === 'rejected') && (
              <button style={S.addBtn} onClick={sendFriendRequest}>Add Friend</button>
            )}
            {!partner.areFriends && friendReq?.status === 'sent' && (
              <button style={{ ...S.addBtn, background: theme.colors.primary, fontSize: 10 }} onClick={sendFriendRequest}>Resend ↺</button>
            )}
            {friendReq?.status === 'accepted' && <span style={S.frStatus}>Friends! 🎉</span>}
            {partner.areFriends && partner.id && (
              <button style={S.dmBtn} onClick={() => navigate(`/chat/${partner.id}`)}>Message</button>
            )}
            <button style={S.iBtnDisabled} title="Voice call"
              onClick={() => showMsg(partner.areFriends ? 'Please go to the Chat page to make calls' : 'Add this person as a friend first to make calls', 3000)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
            </button>
            <button style={S.iBtnDisabled} title="Video call"
              onClick={() => showMsg(partner.areFriends ? 'Please go to the Chat page to make calls' : 'Add this person as a friend first to make calls', 3000)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
            </button>
          </>)}
          {status === STATUS.SEARCHING && <button style={S.endBtn} onClick={endChat}>Cancel</button>}
        </div>
      </div>

      {friendReq?.status === 'received' && !partner?.areFriends && (
        <div style={S.frBanner}>
          <span>Friend request from <strong>{partner?.username}</strong></span>
          <div style={{ display:'flex', gap:8 }}>
            <button style={S.acceptBtn} onClick={() => handleFriendResponse(true)}>Accept</button>
            <button style={S.rejectBtn} onClick={() => handleFriendResponse(false)}>Decline</button>
          </div>
        </div>
      )}

      <div style={S.chatArea}>
        {status === STATUS.IDLE && (
          <div style={S.idle}>
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke={theme.colors.primaryHover} strokeWidth="1.5"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/></svg>
            <h2 style={S.idleTitle}>Meet Someone New</h2>
            <p style={S.idleSub}>Connect with a random stranger</p>
            <button style={S.startBtn} onClick={startChat}>Start Chat</button>
          </div>
        )}
        {status === STATUS.SEARCHING && (
          <div style={S.idle}>
            <div style={{ fontSize: 46, animation:'spin 1.2s linear infinite', display:'inline-block' }}>⟳</div>
            <p style={{ color:theme.colors.primary, fontSize: 15, margin:'16px 0 20px' }}>Finding someone for you...</p>
            <button style={S.cancelBtn} onClick={endChat}>Cancel</button>
          </div>
        )}
        {status === STATUS.CHATTING && messages.length === 0 && (
          <div style={{ padding:20, textAlign:'center', color:'#64748b' }}>Connected! Start typing...</div>
        )}
        {_globalStatus === STATUS.IDLE && partner === null && messages.length > 0 && (
          <div style={{ padding:20, textAlign:'center', color:'#ef4444', background:'rgba(239,68,68,0.05)', borderRadius:12, margin:16 }}>
            <span style={{ fontSize: 18, display:'block', marginBottom:8 }}>🚪</span>
            <strong>Chat Ended</strong>
            <p style={{ fontSize: 11, opacity:0.7, marginTop:4 }}>The other person has left the conversation.</p>
          </div>
        )}
        {status === STATUS.CHATTING && (
          <div style={S.msgs}>
            {messages.map(msg => {
              const isMine = msg.sender_id === myId();
              const isImg  = msg.file_type?.startsWith('image/');
              const isVid  = msg.file_type?.startsWith('video/');
              const isFile = msg.file_url && !isImg && !isVid;
              const fileUrl= resolveUrl(msg.file_url);
              return (
                <div key={msg.id} style={{ ...S.row, justifyContent: isMine ? 'flex-end' : 'flex-start' }}
                  onContextMenu={e => { e.preventDefault(); if (isMine) { const ok = (Date.now() - new Date(msg.timestamp).getTime()) / 1000 <= 15; setCtxMenu({ msgId: msg.id, x: e.clientX, y: e.clientY, canUnsend: ok }); } }}>
                  {!isMine && <Avatar url={null} name={partner?.username} size={28} />}
                  <div style={{ ...S.bbl, ...(isMine ? S.mine : S.theirs), ...(msg.is_unsent ? S.unsent : {}) }}>
                    {msg.is_unsent ? <em style={{ opacity:0.6, fontSize: 11 }}>Unsent</em> : (<>
                      {isImg  && <img src={fileUrl} alt="img" style={S.mImg} onClick={() => setLightbox(fileUrl)} onError={e => e.target.style.display='none'} />}
                      {isVid  && <RCVideoPlayer src={fileUrl} style={S.mVid} />}
                      {isFile && <a href={fileUrl} target="_blank" rel="noreferrer" style={S.fLink}>📎 {msg.original_filename || 'File'}</a>}
                      {msg.content && <span style={S.mTxt}>{msg.content}</span>}
                    </>)}
                    <div style={S.meta}>
                      <span style={S.time}>{new Date(msg.timestamp).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}</span>
                      {isMine && !msg.is_unsent && <span style={{ fontSize:10, color: msg.is_read ? '#22c55e' : '#94a3b8', marginLeft: 3 }}>✓✓</span>}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {status === STATUS.CHATTING && (
        <div className="random-input-wrap" style={{ display:'flex', flexDirection:'column' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 14px', background:'#13131f', borderTop:`1px solid rgba(${theme.colors.primaryRGB},0.15)`, gap:8 }}>
            <button type="button" style={S.navBtn} onClick={nextChat}>⟳ New</button>
            <button type="button" style={S.endCenterBtn} onClick={endChat}>End Chat</button>
            <button type="button" style={S.navBtn} onClick={nextChat}>Next ›</button>
          </div>
          {partnerIsTyping && (
            <div style={{ padding:'4px 14px', fontSize: 10, color:theme.colors.primary, fontStyle:'italic', background:'#13131f' }}>{partner?.username || 'Stranger'} is typing...</div>
          )}
          <form style={{ ...S.inputRow, background:'#111118', borderTop:`1px solid rgba(${theme.colors.primaryRGB},0.15)` }} onSubmit={sendMessage}>
            <input ref={fileInputRef} type="file" style={{ display:'none' }} accept="image/*,video/*,.pdf,.doc,.docx,.txt"
              onChange={e => { if (e.target.files[0]) sendFile(e.target.files[0]); e.target.value = ''; }} />
            <button type="button" style={{ ...S.attachBtn, opacity: uploading ? 0.6 : 1 }} onClick={() => fileInputRef.current?.click()}>
              {uploading ? <span style={{ animation:'spin 1s linear infinite', display:'inline-block' }}>⟳</span>
                : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>}
            </button>
            <textarea style={{ ...S.msgIn, resize: 'none', height: 'auto', minHeight: '38px', maxHeight: '100px', fontFamily: 'inherit' }} rows={1} value={newMsg}
              onChange={e => { setNewMsg(e.target.value); handleTyping(); }}
              placeholder={status === STATUS.CHATTING ? "Type a message..." : "Chat ended"}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(e); } }}
              autoFocus />
            <button type="submit" onMouseDown={e => e.preventDefault()} onTouchStart={e => e.preventDefault()} style={{ ...S.sendBtn, opacity: newMsg.trim() && status === STATUS.CHATTING ? 1 : 0.5 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </button>
          </form>
        </div>
      )}

      {ctxMenu && (
        <div style={{ ...S.ctx, left: Math.min(ctxMenu.x, window.innerWidth-180), top: Math.min(ctxMenu.y, window.innerHeight-120) }} onClick={e => e.stopPropagation()}>
          {ctxMenu.canUnsend && <button style={S.ctxItem} onClick={() => unsendMessage(ctxMenu.msgId)}>Unsend for everyone</button>}
          <button style={{ ...S.ctxItem, color: '#ef4444' }} onClick={() => deleteMessage(ctxMenu.msgId)}>Delete message</button>
        </div>
      )}

      <CallOverlay callState={rtc.callState} callType={rtc.callType} callerName={rtc.callerName}
        isMuted={rtc.isMuted} isVideoOff={rtc.isVideoOff}
        localVideoRef={rtc.localVideoRef} remoteVideoRef={rtc.remoteVideoRef}
        onAccept={rtc.acceptPendingCall} onReject={rtc.rejectCall} onEnd={rtc.endCall}
        onToggleMute={rtc.toggleMute} onToggleVideo={rtc.toggleVideo} />

      {showTrimmer && pendingFile && (
        <VideoTrimmer
          file={pendingFile}
          limit={getLimit(pendingFile)}
          onDone={handleTrimmerDone}
          onCancel={() => { setShowTrimmer(false); setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
        />
      )}
    </div>
  );
}

const S = {
  root: { display:'flex', flexDirection:'column', height:'100%', background:'#0d0d14', position:'relative', overflow:'hidden' },
  notif: { background:`rgba(${theme.colors.primaryRGB},0.22)`, borderBottom:`1px solid rgba(${theme.colors.primaryRGB},0.4)`, color:theme.colors.primary, padding:'8px 16px', fontSize: 11, textAlign:'center', flexShrink:0, fontWeight:500 },
  header: { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderBottom:`1px solid rgba(${theme.colors.primaryRGB},0.18)`, background:'#13131f', flexShrink:0, gap:8, flexWrap:'wrap', minHeight:56 },
  headerLeft: { display:'flex', alignItems:'center', gap:10, minWidth:0, flex:1 },
  title: { fontSize: 15, fontWeight:700, color:theme.colors.primary, letterSpacing:'-0.3px' },
  partnerName: { fontSize: 13, fontWeight:600, color:'#f1f5f9', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  partnerSub: { fontSize: 10, color:'#64748b', marginTop:2, display:'flex', alignItems:'center', gap:4 },
  hActions: { display:'flex', gap:5, alignItems:'center', flexShrink:0 },
  addBtn: { padding:'6px 12px', borderRadius:8, border:'none', background:theme.colors.gradient, color:'#fff', cursor:'pointer', fontSize: 10, fontWeight:600, whiteSpace:'nowrap' },
  dmBtn: { padding:'6px 12px', borderRadius:8, border:`1px solid rgba(${theme.colors.primaryRGB},0.5)`, background:`rgba(${theme.colors.primaryRGB},0.08)`, color:theme.colors.primary, cursor:'pointer', fontSize: 10, whiteSpace:'nowrap' },
  frStatus: { fontSize: 10, color:'#4ade80', padding:'5px 10px', borderRadius:8, background:'rgba(74,222,128,0.1)', whiteSpace:'nowrap' },
  iBtn: { background:`rgba(${theme.colors.primaryRGB},0.1)`, border:`1px solid rgba(${theme.colors.primaryRGB},0.25)`, borderRadius:8, padding:'7px 9px', cursor:'pointer', color:theme.colors.primaryHover, display:'flex', alignItems:'center' },
  iBtnDisabled: { background:'#111118', border:`1px solid rgba(${theme.colors.primaryRGB},0.08)`, borderRadius:8, padding:'7px 9px', cursor:'not-allowed', color:'#334155', display:'flex', alignItems:'center', opacity:0.5 },
  endBtn: { padding:'6px 12px', borderRadius:8, border:'1px solid rgba(239,68,68,0.45)', background:'rgba(239,68,68,0.06)', color:'#f87171', cursor:'pointer', fontSize: 10, whiteSpace:'nowrap' },
  frBanner: { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 16px', background:`rgba(${theme.colors.primaryRGB},0.1)`, borderBottom:`1px solid rgba(${theme.colors.primaryRGB},0.25)`, fontSize: 12, color:theme.colors.primaryHover, gap:12, flexWrap:'wrap', flexShrink:0 },
  acceptBtn: { padding:'5px 14px', borderRadius:8, border:'none', background:'#22c55e', color:'#fff', cursor:'pointer', fontSize: 11, fontWeight:600 },
  rejectBtn: { padding:'5px 14px', borderRadius:8, border:'1px solid rgba(239,68,68,0.5)', background:'transparent', color:'#f87171', cursor:'pointer', fontSize: 11 },
  chatArea: { flex:1, overflow:'hidden', display:'flex', flexDirection:'column', background:'#0d0d14' },
  idle: { flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:6, padding:24 },
  idleTitle: { color:'#f1f5f9', fontSize: 20, fontWeight:700, margin:'8px 0 4px' },
  idleSub: { color:'#64748b', fontSize: 13, margin:'0 0 20px' },
  startBtn: { padding:'14px 36px', borderRadius:14, border:'none', background:theme.colors.gradient, color:'#fff', fontSize: 15, fontWeight:700, cursor:'pointer', boxShadow:`0 4px 24px rgba(${theme.colors.primaryRGB},0.35)` },
  cancelBtn: { padding:'9px 22px', borderRadius:10, border:'1px solid #334155', background:'none', color:'#94a3b8', fontSize: 12, cursor:'pointer' },
  msgs: { flex:1, padding:'14px 14px 8px', display:'flex', flexDirection:'column', gap:6, overflowY:'auto' },
  row: { display:'flex', alignItems:'flex-end', gap:8 },
  bbl: { padding:'10px 14px', borderRadius:18, wordBreak:'break-word', maxWidth:'min(75%,320px)', boxShadow:'0 2px 8px rgba(0,0,0,0.25)' },
  mine: { background:'#334155', color:'#fff', borderBottomRightRadius:4 },
  theirs: { background:'#1e1e2e', color:'#e2e8f0', borderBottomLeftRadius:4, border:`1px solid rgba(${theme.colors.primaryRGB},0.1)` },
  unsent: { background:'#16161d', border:'1px dashed #334155' },
  mTxt: { fontSize: 12, lineHeight:1.55, display:'inline' },
  meta: { display:'inline-flex', alignItems:'center', gap:4, marginLeft:8, marginTop:6, float:'right' },
  time: { fontSize:10, opacity:0.6 },
  mImg: { maxWidth:'100%', maxHeight:240, borderRadius:12, cursor:'pointer', display:'block', objectFit:'cover' },
  mVid: { maxWidth:'100%', maxHeight:200, borderRadius:12, display:'block' },
  fLink: { color:theme.colors.primary, textDecoration:'none', fontSize: 11, display:'flex', alignItems:'center', gap:5 },
  inputRow: { display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'#13131f', flexShrink:0 },
  attachBtn: { background:`rgba(${theme.colors.primaryRGB},0.1)`, border:`1px solid rgba(${theme.colors.primaryRGB},0.25)`, borderRadius:10, padding:'9px 11px', cursor:'pointer', color:theme.colors.primary, display:'flex', alignItems:'center', flexShrink:0 },
  msgIn: { flex:1, padding:'10px 14px', borderRadius:12, border:`1px solid rgba(${theme.colors.primaryRGB},0.25)`, background:'#1a1a2e', color:'#f1f5f9', fontSize: 12, outline:'none', minWidth:0 },
  sendBtn: { background:theme.colors.gradient, border:'none', borderRadius:10, padding:'9px 12px', cursor:'pointer', color:'#fff', flexShrink:0, display:'flex', alignItems:'center', boxShadow:`0 2px 10px rgba(${theme.colors.primaryRGB},0.3)` },
  ctx: { position:'fixed', background:'#1e1e2e', border:`1px solid rgba(${theme.colors.primaryRGB},0.3)`, borderRadius:10, padding:6, zIndex:1000, boxShadow:'0 8px 24px rgba(0,0,0,0.6)', minWidth:160 },
  ctxItem: { display:'block', width:'100%', padding:'8px 12px', background:'none', border:'none', color:'#e2e8f0', cursor:'pointer', textAlign:'left', fontSize: 12, borderRadius:6 },
  navBtn: { padding:'7px 16px', borderRadius:8, background:`rgba(${theme.colors.primaryRGB},0.1)`, border:`1px solid rgba(${theme.colors.primaryRGB},0.3)`, color:theme.colors.primary, cursor:'pointer', fontSize: 11, fontWeight:600, flex:1, maxWidth:100 },
  endCenterBtn: { padding:'7px 16px', borderRadius:8, background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.35)', color:'#f87171', cursor:'pointer', fontSize: 11, fontWeight:600, flex:1, maxWidth:110 },
};
